import { ExhibitId } from '../store/slices/exhibitSlice';

// Get exhibit name for display
export const getExhibitName = (exhibitId: ExhibitId): string => {
    switch (exhibitId) {
        case 'orang-laut': return 'Orang Laut & Sea Trade';
        case 'book-of-myths': return 'Book of Myths & Legends';
        case 'sg60': return 'SG60';
        case 'market-traders': return 'Early Market Traders';
        case 'badang': return 'Legend of Badang';
        case 'peranakan-design': return 'Peranakan Design';
        case 'time-capsule': return 'Time Capsule';
        default: return 'This Experience';
    }
};

// Scroll to top of page
export const scrollToTop = (element: HTMLElement, top: number = 0, left: number = 0, behavior: 'smooth' | 'instant' = 'smooth') => {
    element.scrollTo({ top: top, left: left, behavior: behavior });
};

// Enhanced custom scroll function with both X and Y control  e.g. smoothScrollTo(element, null, 0, 300, "normal");
export const smoothScrollTo = (
    element: HTMLElement, 
    targetX: number | null = null, 
    targetY: number | null = null, 
    duration: number = 600,  // 600ms duration for smooth scroll
    easing: 'normal' | 'ease-in' | 'ease-out' = 'normal'  // Easing type
) => {
    const startX = element.scrollLeft;
    const startY = element.scrollTop;

    // Only calculate distance for axes that have targets
    const distanceX = targetX !== null ? targetX - startX : 0;
    const distanceY = targetY !== null ? targetY - startY : 0;

    const startTime = performance.now();

    // Easing functions
    const getEasedProgress = (progress: number): number => {
        switch (easing) {
            case 'ease-in':
                // Cubic ease-in: starts slow, accelerates
                return progress * progress * progress;
            case 'ease-out':
                // Cubic ease-out: starts fast, decelerates
                return 1 - Math.pow(1 - progress, 3);
            case 'normal':
            default:
                // Linear: constant speed
                return progress;
        }
    };

    const step = (currentTime: number) => {
        const elapsedTime = currentTime - startTime;
        const progress = Math.min(elapsedTime / duration, 1);

        // Apply easing function
        const easedProgress = getEasedProgress(progress);

        // Update scroll positions only for specified axes
        if (targetX !== null) {
            element.scrollLeft = startX + distanceX * easedProgress;
        }
        if (targetY !== null) {
            element.scrollTop = startY + distanceY * easedProgress;
        }

        if (progress < 1) {
            requestAnimationFrame(step);
        }
    };

    requestAnimationFrame(step);
};

/**
 * Compress an image to ensure it's under the specified file size limit
 * @param canvas - The canvas element containing the image
 * @param maxSizeKB - Maximum file size in KB (default: 1024KB = 1MB)
 * @param initialQuality - Initial compression quality (0.1 to 1.0)
 * @param minQuality - Minimum quality threshold (default: 0.3)
 * @returns Promise<string> - Base64 data URL of compressed image
 */
export const compressImage = async (
    canvas: HTMLCanvasElement,
    maxSizeKB: number = 1024,
    initialQuality: number = 0.8,
    minQuality: number = 0.3
): Promise<string> => {
    let quality = initialQuality;
    let dataUrl = '';
    let fileSizeKB = 0;

    // Function to get file size in KB from data URL
    const getFileSizeKB = (dataUrl: string): number => {
        // Remove data URL prefix to get just the base64 data
        const base64 = dataUrl.split(',')[1];
        // Calculate approximate file size (base64 is ~33% larger than binary)
        return (base64.length * 0.75) / 1024;
    };

    // Try JPEG compression first (much smaller than PNG)
    do {
        dataUrl = canvas.toDataURL('image/jpeg', quality);
        fileSizeKB = getFileSizeKB(dataUrl);
        
        // If still too large and quality is above minimum, reduce quality
        if (fileSizeKB > maxSizeKB && quality > minQuality) {
            quality -= 0.1;
        } else {
            break;
        }
    } while (quality >= minQuality);

    // If JPEG compression isn't enough, try resizing the canvas
    if (fileSizeKB > maxSizeKB) {
        const scaleFactor = Math.sqrt(maxSizeKB / fileSizeKB);
        const newWidth = Math.floor(canvas.width * scaleFactor);
        const newHeight = Math.floor(canvas.height * scaleFactor);
        
        // Create a new smaller canvas
        const resizedCanvas = document.createElement('canvas');
        resizedCanvas.width = newWidth;
        resizedCanvas.height = newHeight;
        
        const ctx = resizedCanvas.getContext('2d');
        if (ctx) {
            // Enable image smoothing for better quality when resizing
            ctx.imageSmoothingEnabled = true;
            ctx.imageSmoothingQuality = 'high';
            
            // Draw the original canvas onto the smaller canvas
            ctx.drawImage(canvas, 0, 0, newWidth, newHeight);
            
            // Try compression again with the resized image
            quality = initialQuality;
            do {
                dataUrl = resizedCanvas.toDataURL('image/jpeg', quality);
                fileSizeKB = getFileSizeKB(dataUrl);
                
                if (fileSizeKB > maxSizeKB && quality > minQuality) {
                    quality -= 0.1;
                } else {
                    break;
                }
            } while (quality >= minQuality);
        }
    }

    console.log(`Image compressed to ${Math.round(fileSizeKB)}KB with quality ${quality.toFixed(1)}`);
    return dataUrl;
};

/**
 * Get the file size of a base64 data URL in KB
 * @param dataUrl - Base64 data URL
 * @returns File size in KB
 */
export const getDataUrlSizeKB = (dataUrl: string): number => {
    const base64 = dataUrl.split(',')[1];
    return (base64.length * 0.75) / 1024;
};

/**
 * Check for visitor ID mismatch between Redux state and cookies
 * Returns the correct visitor ID to use (prioritizes cookie over Redux)
 */
export function checkVisitorIdSync(reduxVisitorId: string): { 
    visitorId: string; 
    needsSync: boolean; 
    cookieVisitorId?: string;
} {
    // Parse cookies to find visitor ID
    const cookies = document.cookie.split(';').reduce((acc, cookie) => {
        const [key, value] = cookie.trim().split('=');
        acc[key] = value;
        return acc;
    }, {} as Record<string, string>);
    
    const cookieVisitorId = cookies['v'];
    
    if (!cookieVisitorId) {
        console.log('🍪 No visitor ID cookie found, using Redux value');
        return { visitorId: reduxVisitorId, needsSync: false };
    }
    
    if (cookieVisitorId !== reduxVisitorId) {
        console.warn('⚠️ Visitor ID mismatch detected:', {
            reduxVisitorId,
            cookieVisitorId,
            resolution: 'Using cookie value (backend source of truth)'
        });
        return { 
            visitorId: cookieVisitorId, 
            needsSync: true, 
            cookieVisitorId 
        };
    }
    
    console.log('✅ Visitor ID sync check passed');
    return { visitorId: reduxVisitorId, needsSync: false };
}

/**
 * Get all cookies as an object for debugging
 */
export function getAllCookies(): Record<string, string> {
    return document.cookie.split(';').reduce((acc, cookie) => {
        const [key, value] = cookie.trim().split('=');
        if (key && value) {
            acc[key] = value;
        }
        return acc;
    }, {} as Record<string, string>);
}

/**
 * Debug function to analyze Redux DevTools and identify unexpected state slices
 * This helps identify if external sources are injecting state into Redux
 */
export function debugReduxState() {
    // Expected Time Capsule state slices
    const expectedSlices = ['user', 'exhibit', 'navigation', 'zones', '_persist'];
    
    // Check if Redux DevTools extension is available
    const reduxDevTools = (window as any).__REDUX_DEVTOOLS_EXTENSION__;
    
    console.group('🔍 Redux State Analysis');
    
    if (reduxDevTools) {
        console.log('✅ Redux DevTools detected');
        
        // Try to get the current state from the store
        try {
            // Access the store if it's available globally
            const store = (window as any).store || (window as any).__REDUX_STORE__;
            
            if (store && store.getState) {
                const state = store.getState();
                const actualSlices = Object.keys(state);
                
                console.log('📊 Current Redux State Slices:', actualSlices);
                console.log('🎯 Expected Time Capsule Slices:', expectedSlices);
                
                const unexpectedSlices = actualSlices.filter(slice => !expectedSlices.includes(slice));
                const missingSlices = expectedSlices.filter(slice => !actualSlices.includes(slice));
                
                if (unexpectedSlices.length > 0) {
                    console.warn('⚠️ Unexpected state slices detected:', unexpectedSlices);
                    console.log('💡 These slices may come from:');
                    console.log('   - Browser extensions (video downloaders, ad blockers, etc.)');
                    console.log('   - Other React apps on the same page');
                    console.log('   - Third-party scripts using Redux');
                } else {
                    console.log('✅ All state slices are expected');
                }
                
                if (missingSlices.length > 0) {
                    console.warn('❌ Missing expected slices:', missingSlices);
                }
                
            } else {
                console.log('ℹ️ Store not accessible for direct analysis');
            }
        } catch (error) {
            console.log('ℹ️ Could not analyze store directly:', error);
        }
    } else {
        console.log('❌ Redux DevTools not detected');
    }
    
    // Check for multiple Redux stores
    const possibleStores = [];
    if ((window as any).store) possibleStores.push('window.store');
    if ((window as any).__REDUX_STORE__) possibleStores.push('window.__REDUX_STORE__');
    if ((window as any).__STORE__) possibleStores.push('window.__STORE__');
    
    console.log('🏪 Detected Redux stores:', possibleStores.length > 0 ? possibleStores : 'None found globally');
    
    // Check for browser extensions that might use Redux
    const suspiciousGlobals: string[] = [];
    const commonExtensionNames = ['__EXTENSION__', '__REACT_DEVTOOLS__', '__APOLLO_CLIENT__', '__MOBX__'];
    
    commonExtensionNames.forEach(name => {
        if ((window as any)[name]) {
            suspiciousGlobals.push(name);
        }
    });
    
    if (suspiciousGlobals.length > 0) {
        console.log('🔌 Detected extension globals:', suspiciousGlobals);
    }
    
    console.groupEnd();
}

/**
 * Monitor Redux state changes to detect when unexpected slices are added
 */
export function monitorReduxStateChanges() {
    const expectedSlices = ['user', 'exhibit', 'navigation', 'zones', '_persist'];
    let lastKnownSlices: string[] = [];
    
    const checkInterval = setInterval(() => {
        try {
            const store = (window as any).store || (window as any).__REDUX_STORE__;
            if (store && store.getState) {
                const state = store.getState();
                const currentSlices = Object.keys(state);
                
                // Check if slices have changed
                if (JSON.stringify(currentSlices.sort()) !== JSON.stringify(lastKnownSlices.sort())) {
                    const newSlices = currentSlices.filter(slice => !lastKnownSlices.includes(slice));
                    const removedSlices = lastKnownSlices.filter(slice => !currentSlices.includes(slice));
                    
                    if (newSlices.length > 0) {
                        const unexpectedNew = newSlices.filter(slice => !expectedSlices.includes(slice));
                        if (unexpectedNew.length > 0) {
                            console.warn('🚨 New unexpected Redux slices detected:', unexpectedNew);
                        }
                    }
                    
                    if (removedSlices.length > 0) {
                        console.log('📤 Redux slices removed:', removedSlices);
                    }
                    
                    lastKnownSlices = [...currentSlices];
                }
            }
        } catch (error) {
            // Silently ignore errors to avoid spam
        }
    }, 2000); // Check every 2 seconds
    
    // Return cleanup function
    return () => clearInterval(checkInterval);
}

/**
 * Detect if the app is running as a PWA on Android
 */
export function isAndroidPWA(): boolean {
    // Check if running in standalone mode (PWA)
    const isStandalone = window.matchMedia('(display-mode: standalone)').matches ||
                        (window.navigator as any).standalone ||
                        document.referrer.includes('android-app://');
    
    // Check if running on Android
    const isAndroid = /Android/i.test(navigator.userAgent);
    
    // Check for Samsung Internet specifically
    const isSamsungInternet = /SamsungBrowser/i.test(navigator.userAgent);
    
    return isStandalone && isAndroid;
}

/**
 * Detect if running on Samsung Internet browser
 */
export function isSamsungInternet(): boolean {
    return /SamsungBrowser/i.test(navigator.userAgent);
}

/**
 * Apply Android PWA specific fixes for background image issues
 */
export function applyAndroidPWAFixes(): void {
    if (!isAndroidPWA()) {
        return;
    }
    
    console.log('🤖 Android PWA detected - applying background fixes...');
    
    // Create and inject CSS fixes specifically for Android PWA
    const style = document.createElement('style');
    style.id = 'android-pwa-fixes';
    
    const css = `
        /* Android PWA Background Fix - Dynamic */
        .bg-image-galaxy {
            background-attachment: scroll !important;
            min-height: 100vh !important;
            min-height: 100dvh !important;
        }
        
        .main-content-wrapper.bg-image-galaxy {
            height: 100vh !important;
            height: 100dvh !important;
            min-height: 100vh !important;
            min-height: 100dvh !important;
            padding-bottom: ${window.screen.height * 0.1}px !important;
        }
        
        /* Samsung Internet specific fixes */
        ${isSamsungInternet() ? `
            .bg-image-galaxy {
                background-size: cover !important;
                background-position: center center !important;
                background-repeat: no-repeat !important;
            }
            
            .main-content-wrapper.bg-image-galaxy {
                position: fixed !important;
                top: 0 !important;
                left: 0 !important;
                right: 0 !important;
                bottom: 0 !important;
                width: 100vw !important;
                height: 100vh !important;
                min-height: 100vh !important;
                overflow-y: auto !important;
                overflow-x: hidden !important;
            }
        ` : ''}
    `;
    
    style.textContent = css;
    document.head.appendChild(style);
    
    // Add a class to body for additional targeting
    document.body.classList.add('android-pwa');
    if (isSamsungInternet()) {
        document.body.classList.add('samsung-internet');
    }
    
    console.log('✅ Android PWA fixes applied', {
        isAndroid: /Android/i.test(navigator.userAgent),
        isSamsungInternet: isSamsungInternet(),
        screenHeight: window.screen.height,
        viewportHeight: window.innerHeight,
        backgroundFix: 'Applied: scroll attachment + dynamic viewport'
    });
}

/**
 * Remove Android PWA fixes (for cleanup)
 */
export function removeAndroidPWAFixes(): void {
    const existingStyle = document.getElementById('android-pwa-fixes');
    if (existingStyle) {
        existingStyle.remove();
    }
    
    document.body.classList.remove('android-pwa', 'samsung-internet');
    
    console.log('🧹 Android PWA fixes removed');
}